import {
  List,
  AutoSizer,
  ListRowProps,
  CellMeasurerCache,
  CellMeasurer,
  InfiniteLoader,
} from "react-virtualized";
import React, { useCallback, useMemo } from "react";
import { observer } from "mobx-react";
import { Center } from "@chakra-ui/react";
import { EmptyScreen } from "components";
import { AccountAnalyticsStore } from "store/UserAccounts/UserAccount/AccountAnalytics/AccountAnalytics.store";
import { LeadAttributionPeopleListItem } from "./LeadAttributionPeopleListItem";

interface LeadActivityListProps {
  accountAnalyticsStore: AccountAnalyticsStore;
}

const cache = new CellMeasurerCache({
  fixedWidth: true,
  defaultHeight: 100,
});

export const LeadAttributionPeopleList: React.FC<LeadActivityListProps> = observer(
  ({ accountAnalyticsStore }) => {
    const {
      accountLeadAttributionPeopleStore,
    } = accountAnalyticsStore.accountAnalyticsLeadAttributionStore;

    const {
      people,
      shouldLoadNextBatch: hasNextPage,
      fetchLeadAttributionPeople,
    } = accountLeadAttributionPeopleStore;

    const rowRenderer = useCallback(
      ({ key, index, parent, style }: ListRowProps) => {
        const person = people[index];
        if (!person) return null;
        return (
          <CellMeasurer cache={cache} index={index} parent={parent} key={key}>
            {({ measure }) => (
              <LeadAttributionPeopleListItem
                key={person.id}
                person={person}
                style={style}
                onLoad={measure}
              />
            )}
          </CellMeasurer>
        );
      },
      [people]
    );

    const fetchMoreRows = useCallback(() => {
      return fetchLeadAttributionPeople();
    }, [fetchLeadAttributionPeople]);

    const rowCount = useMemo(() => {
      return hasNextPage ? people.length + 1 : people.length;
    }, [people.length, hasNextPage]);

    return (
      <InfiniteLoader
        isRowLoaded={({ index }) => index < people.length}
        loadMoreRows={fetchMoreRows}
        rowCount={rowCount}
        threshold={1}
      >
        {({ registerChild, onRowsRendered }) => (
          <AutoSizer>
            {({ width, height }) => {
              return (
                <List
                  ref={registerChild}
                  width={width}
                  height={height}
                  rowCount={rowCount}
                  deferredMeasurementCache={cache}
                  rowHeight={cache.rowHeight}
                  rowRenderer={rowRenderer}
                  onRowsRendered={onRowsRendered}
                  overscanRowCount={4}
                  noRowsRenderer={() => (
                    <Center height={height}>
                      <EmptyScreen label={"No people."} />
                    </Center>
                  )}
                />
              );
            }}
          </AutoSizer>
        )}
      </InfiniteLoader>
    );
  }
);
